import { FluentBundle } from 'fluent';
import { GetString } from 'fluent-react';
import React, { RefObject } from 'react';
import styled from 'styled-components';
import { CountryProfile, PolicyRecommendation } from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import {PolicyDimensionsContainer as Root} from '../Grid';
import { PolicyDimension } from '../Utils';
import PolicyContainer from './PolicyContainer';

const query = gql`
  query FeasibilityTopPanel($countryId: ID!) {
    focusedCountry: countryProfile(location: $countryId) {
      policyRecommendation
      latestEciRank
    }
  }
`;

interface Variables {
  countryId: string;
}

interface SuccessResponse {
  focusedCountry: null | {
    policyRecommendation: CountryProfile['policyRecommendation'],
    latestEciRank: CountryProfile['latestEciRank'],
  };
}

const gridHeight = '6.4rem';

const PanelContainer = styled.div`
  box-shadow: 1px 1px 2px 1px #a7a6a62b;
  border-bottom: 1px solid #0000000d;
  display: grid;
  grid-template-columns: 1fr minmax(250px, 290px) minmax(250px, 290px)  minmax(200px, 290px) 1fr;
  grid-template-rows: 1rem ${gridHeight} 1rem;
  grid-gap: 0 15px;

  @media (max-height: 700px) {
    grid-template-columns: 1fr minmax(auto, 290px) 185px  minmax(100px, 290px) 1fr;
    grid-template-rows: 0.2rem 5rem 0.2rem;
  }

  @media screen and (max-width: 1200px), screen and (max-height: 700px) {
    font-size: 0.8rem;
    grid-gap: 0 6px;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 0 minmax(auto, 290px) 185px  minmax(100px, 290px) 60px;
  }

  @media (max-width: 950px) {
    box-shadow: none;
    border-bottom: none;
  }
`;

interface Props extends SlideElementProps {
  countryId: string;
  setPolicyDimension: (value: PolicyDimension) => void;
  getFluentString: GetString;
  policyDimension: PolicyDimension;
  feasibilityTopPanelZIndex: number;
  promoteZIndex: () => void;
  restoreZIndex: () => void;
  fluentBundle: FluentBundle;
}

const TopPanel = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    countryId, setPolicyDimension,
    getFluentString, policyDimension, feasibilityTopPanelZIndex,
    promoteZIndex, restoreZIndex,
    fluentBundle,
  } = props;
  const variables: Variables = {
    countryId,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {focusedCountry} = data;
    if (focusedCountry !== null &&
        focusedCountry.policyRecommendation !== null && focusedCountry.latestEciRank !== null) {
      if (focusedCountry.policyRecommendation === PolicyRecommendation.TechFrontier) {
        output = null;
      } else {
        output = (
          <PanelContainer>
            <PolicyContainer
              policyDimension={policyDimension}
              setPolicyDimension={setPolicyDimension}
              policyRecommendation={focusedCountry.policyRecommendation}
              getFluentString={getFluentString}
              fluentBundle={fluentBundle}
              promoteZIndex={promoteZIndex}
              restoreZIndex={restoreZIndex}
            />
          </PanelContainer>
        );
      }
    } else {
      output = null;
    }
  } else {
    output = null;
  }

  const style: React.CSSProperties = {
    zIndex: feasibilityTopPanelZIndex,
  };

  return (
    <Root
      style={style}
      ref={rootElRef}
    >
      {output}
    </Root>
  );
});

export default TopPanel;
